import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Alert,
  Spinner,
  Modal,
  Accordion,
  Form,
} from 'react-bootstrap';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import PageWrapper from 'components/PageWrapper';
import InnkjopSyncApi from 'services/KjederegisteretAdminApi/InnkjopSyncApi';
import {
  InnkAutoSyncRunDto,
  InnkjopPreviewEndring,
  InnkjopPreviewResultDto,
} from 'services/KjederegisteretAdminApi/InnkjopSyncApi/types';

const formatDate = (date: Date | null | undefined) => {
  if (!date) return '–';
  return format(new Date(date), 'dd.MM.yyyy');
};

const formatDateTime = (date: Date | null | undefined) => {
  if (!date) return '–';
  return format(new Date(date), 'dd.MM.yyyy HH:mm');
};

const AutoSyncStatusBadge = ({ status }: { status: InnkAutoSyncRunDto['status'] }) => {
  if (status === 'Succeeded') return <Badge bg="success">Fullført</Badge>;
  if (status === 'Failed') return <Badge bg="danger">Feilet</Badge>;
  return <Badge bg="secondary">Pågår</Badge>;
};

const AutoSyncPanel = () => {
  const [runs, setRuns] = useState<InnkAutoSyncRunDto[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    InnkjopSyncApi.getAutoSyncRuns()
      .then(setRuns)
      .catch((e: any) => setError(e?.response?.data?.detail ?? e?.message ?? 'Ukjent feil'))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <div className="mb-3 text-muted small">
        <Spinner animation="border" size="sm" className="me-2" />
        Henter automatisk synkronisering…
      </div>
    );
  }
  if (error) {
    return (
      <Alert variant="warning" className="mb-3">
        Kunne ikke hente historikk for automatisk synkronisering: {error}
      </Alert>
    );
  }
  if (!runs || runs.length === 0) return null;

  const [latest, ...history] = runs;

  return (
    <Card className="mb-4">
      <Card.Header>Automatisk synkronisering (DLVRY)</Card.Header>
      <Card.Body>
        <div className="d-flex align-items-center gap-2 mb-2">
          <AutoSyncStatusBadge status={latest.status} />
          <span className="text-muted small">
            Sist kjørt {formatDateTime(latest.startedUtc)}
            {latest.finishedUtc && ` – fullført ${formatDateTime(latest.finishedUtc)}`}
          </span>
        </div>
        {latest.status === 'Failed' ? (
          <p className="text-danger small mb-2">{latest.error ?? 'Ukjent feil'}</p>
        ) : (
          <p className="mb-2">
            La til <strong>{latest.addedCount}</strong> av {latest.eligibleForAutoSyncCount} enheter
            uten annen innkjøpstilknytning (NHO Reiseliv/KNIF/NORES) ({latest.candidatesFoundCount}{' '}
            DLVRY-kandidater totalt vurdert).
          </p>
        )}
        {latest.items.length > 0 && (
          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>Statistikknr</th>
                <th>Løpenr</th>
                <th>Enhetsnavn</th>
                <th>Fra dato</th>
              </tr>
            </thead>
            <tbody>
              {latest.items.map((item) => (
                <tr key={item.statistikknr}>
                  <td>{item.statistikknr}</td>
                  <td>
                    <Link to={`/enheter/${item.enhetId}`} target="_blank" rel="noopener noreferrer">
                      {item.lopenr ?? '–'}
                    </Link>
                  </td>
                  <td>{item.enhetsnavn}</td>
                  <td>{formatDate(item.fraOgMedDato)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {history.length > 0 && (
          <>
            <Button
              variant="link"
              size="sm"
              className="ps-0"
              onClick={() => setShowHistory((v) => !v)}
            >
              {showHistory
                ? 'Skjul tidligere kjøringer'
                : `Vis tidligere kjøringer (${history.length})`}
            </Button>
            {showHistory && (
              <Table striped bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    <th>Kjørt</th>
                    <th>Status</th>
                    <th>Lagt til</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((run) => (
                    <tr key={run.id}>
                      <td>{formatDateTime(run.startedUtc)}</td>
                      <td>
                        <AutoSyncStatusBadge status={run.status} />
                      </td>
                      <td>{run.addedCount}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

interface AvtaleDetaljerModalProps {
  enhet: InnkjopPreviewEndring | null;
  onClose: () => void;
}

const PAGE_SIZE = 50;

const today = () => new Date().toISOString().slice(0, 10);
const firstOfYear = () => `${new Date().getFullYear()}-01-01`;

const AvtaleDetaljerModal = ({ enhet, onClose }: AvtaleDetaljerModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detaljer, setDetaljer] = useState<unknown>(null);
  const [sammendrag, setSammendrag] = useState<unknown>(null);
  const [fraDato, setFraDato] = useState(firstOfYear());
  const [tilDato, setTilDato] = useState(today());

  const show = enhet !== null;

  const fetchData = (snr: number, fra?: string, til?: string) => {
    setIsLoading(true);
    setError(null);
    setDetaljer(null);
    setSammendrag(null);

    Promise.all([
      InnkjopSyncApi.getFakturaDetaljer(snr, fra || undefined, til || undefined),
      InnkjopSyncApi.getFakturaSammendrag(snr, fra || undefined, til || undefined),
    ])
      .then(([d, s]) => {
        setDetaljer(d);
        setSammendrag(s);
      })
      .catch((e) => setError(e?.response?.data?.detail ?? e?.message ?? 'Ukjent feil'))
      .finally(() => setIsLoading(false));
  };

  React.useEffect(() => {
    if (!enhet) return;
    const fra = firstOfYear();
    const til = today();
    setFraDato(fra);
    setTilDato(til);
    fetchData(enhet.statistikknr, fra, til);
  }, [enhet?.statistikknr]);

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Avtaledetaljer – {enhet?.enhetsnavn ?? ''}{' '}
          <small className="text-muted fs-6">(snr {enhet?.statistikknr})</small>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className="d-flex gap-2 align-items-end mb-3">
          <Form.Group>
            <Form.Label className="mb-1 small">Fra dato</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={fraDato}
              onChange={(e) => setFraDato(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="mb-1 small">Til dato</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={tilDato}
              onChange={(e) => setTilDato(e.target.value)}
            />
          </Form.Group>
          <Button
            size="sm"
            variant="primary"
            disabled={isLoading}
            onClick={() => enhet && fetchData(enhet.statistikknr, fraDato, tilDato)}
          >
            Hent
          </Button>
        </Form>
        {isLoading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {!isLoading && !error && (
          <Accordion defaultActiveKey="0" alwaysOpen>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Fakturasammendrag</Accordion.Header>
              <Accordion.Body>
                <pre style={{ maxHeight: '300px', overflow: 'auto', fontSize: '0.8rem' }}>
                  {JSON.stringify(sammendrag, null, 2)}
                </pre>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>Fakturadetaljer</Accordion.Header>
              <Accordion.Body>
                <pre style={{ maxHeight: '300px', overflow: 'auto', fontSize: '0.8rem' }}>
                  {JSON.stringify(detaljer, null, 2)}
                </pre>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Lukk
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface RowProps {
  row: InnkjopPreviewEndring;
  showDatoer: boolean;
  hasSelection: boolean;
  isSelected: boolean;
  onToggle: (id: number) => void;
  onInfo: (row: InnkjopPreviewEndring) => void;
}

const EnhetsRow = React.memo(
  ({ row, showDatoer, hasSelection, isSelected, onToggle, onInfo }: RowProps) => (
    <tr>
      {hasSelection && (
        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
          <input
            type="checkbox"
            className="form-check-input"
            checked={isSelected}
            onChange={() => onToggle(row.enhetId)}
          />
        </td>
      )}
      <td>{row.statistikknr}</td>
      <td>
        {row.enhetId ? (
          <a href={`/enheter/${row.enhetId}`} target="_blank" rel="noopener noreferrer">
            {row.lopenr ?? '\u2013'}
          </a>
        ) : (
          (row.lopenr ?? '\u2013')
        )}
      </td>
      <td>
        {row.enhetId ? (
          <a href={`/enheter/${row.enhetId}`} target="_blank" rel="noopener noreferrer">
            {row.enhetsnavn}
          </a>
        ) : (
          row.enhetsnavn
        )}
        {row.erAlleredeMedlemAvInnk && (
          <Badge bg="info" className="ms-1" title="Aktiv INNK-medlem">
            INNK
          </Badge>
        )}
        {row.erDlvryGrossist && (
          <Badge bg="warning" text="dark" className="ms-1" title="Tilknyttet DLVRY-grossist">
            DLVRY
          </Badge>
        )}
        {row.erNhoReiselivMedlem && (
          <Badge bg="success" className="ms-1" title="Aktiv NHO Reiseliv Innkjøpskjeden-medlem">
            NHO Reiseliv
          </Badge>
        )}
        {row.erKnifMedlem && (
          <Badge bg="primary" className="ms-1" title="Aktiv KNIF INNKJØP-medlem">
            KNIF
          </Badge>
        )}
        {row.erNoresMedlem && (
          <Badge bg="secondary" className="ms-1" title="Aktiv NORES SA-medlem">
            NORES
          </Badge>
        )}
        {row.erOiMedlem && (
          <Badge bg="dark" className="ms-1" title="Aktiv medlem i OI-type enhetsgruppe">
            OI
          </Badge>
        )}
        {row.nedlagtDato && (
          <Badge bg="secondary" className="ms-1" title={`Nedlagt ${formatDate(row.nedlagtDato)}`}>
            Nedlagt
          </Badge>
        )}
      </td>
      <td>{row.hovedBransje ?? '\u2013'}</td>
      <td>{row.innkjopGrupperingNavn}</td>
      <td>{formatDate(row.fraOgMedDato)}</td>
      {showDatoer && <td>{formatDate(row.oppstartsdato)}</td>}
      <td className="text-muted small">{row.arsak ?? ''}</td>
      <td>
        <Button
          variant="outline-secondary"
          size="sm"
          title="Vis avtaledetaljer"
          onClick={() => onInfo(row)}
        >
          ℹ
        </Button>
      </td>
    </tr>
  ),
);

interface EndringTableProps {
  rows: InnkjopPreviewEndring[];
  emptyText: string;
  onRowClick: (row: InnkjopPreviewEndring) => void;
  showDatoer?: boolean;
  onBulkFjern?: (ids: number[]) => Promise<void>;
  onBulkLeggTil?: (items: Array<{ enhetId: number; fraOgMedDato: Date }>) => Promise<void>;
}

const EndringTable = ({
  rows,
  emptyText,
  onRowClick,
  showDatoer = false,
  onBulkFjern,
  onBulkLeggTil,
}: EndringTableProps) => {
  const [selectedIds, setSelectedIds] = useState(() => new Set<number>());
  const [showConfirm, setShowConfirm] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [sortCol, setSortCol] = useState<keyof InnkjopPreviewEndring | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterGruppering, setFilterGruppering] = useState('');
  const [filterBransje, setFilterBransje] = useState('');
  const [filterHandling, setFilterHandling] = useState('');
  const [filterBadge, setFilterBadge] = useState('');

  const uniqueGrupperinger = useMemo(
    () => Array.from(new Set(rows.map((r) => r.innkjopGrupperingNavn).filter(Boolean))).sort(),
    [rows],
  );
  const uniqueBransjer = useMemo(
    () => Array.from(new Set(rows.map((r) => r.hovedBransje).filter(Boolean))).sort(),
    [rows],
  );
  const uniqueHandlinger = useMemo(
    () => Array.from(new Set(rows.map((r) => r.handling).filter(Boolean))).sort(),
    [rows],
  );

  const handleSort = useCallback((col: keyof InnkjopPreviewEndring) => {
    setSortCol((prev) => {
      if (prev === col) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return col;
    });
  }, []);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      result = result.filter(
        (r) => String(r.statistikknr).includes(q) || (r.enhetsnavn ?? '').toLowerCase().includes(q),
      );
    }
    if (filterGruppering)
      result = result.filter((r) => r.innkjopGrupperingNavn === filterGruppering);
    if (filterBransje) result = result.filter((r) => r.hovedBransje === filterBransje);
    if (filterHandling) result = result.filter((r) => r.handling === filterHandling);
    if (filterBadge === 'DLVRY') result = result.filter((r) => r.erDlvryGrossist);
    if (filterBadge === 'INNK') result = result.filter((r) => r.erAlleredeMedlemAvInnk);
    if (filterBadge === 'NHO') result = result.filter((r) => r.erNhoReiselivMedlem);
    if (filterBadge === 'KNIF') result = result.filter((r) => r.erKnifMedlem);
    if (filterBadge === 'NORES') result = result.filter((r) => r.erNoresMedlem);
    if (filterBadge === 'OI') result = result.filter((r) => r.erOiMedlem);
    if (filterBadge === 'Nedlagt') result = result.filter((r) => !!r.nedlagtDato);
    return result;
  }, [rows, filterSearch, filterGruppering, filterBransje, filterHandling, filterBadge]);

  const sortedRows = useMemo(() => {
    if (!sortCol) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const va = a[sortCol];
      const vb = b[sortCol];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      let cmp: number;
      if (va instanceof Date && vb instanceof Date) {
        cmp = va.getTime() - vb.getTime();
      } else if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), 'nb');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredRows, sortCol, sortDir]);

  const totalPages = Math.ceil(sortedRows.length / PAGE_SIZE);
  const pagedRows = useMemo(
    () => sortedRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [sortedRows, page],
  );

  const isFiltered =
    filterSearch || filterGruppering || filterBransje || filterHandling || filterBadge;

  const isLeggTil = !onBulkFjern && !!onBulkLeggTil;
  const hasSelection = !!(onBulkFjern || onBulkLeggTil);
  const allVisibleSelected = useMemo(
    () =>
      hasSelection && sortedRows.length > 0 && sortedRows.every((r) => selectedIds.has(r.enhetId)),
    [hasSelection, sortedRows, selectedIds],
  );
  const someVisibleSelected = useMemo(
    () => hasSelection && sortedRows.some((r) => selectedIds.has(r.enhetId)),
    [hasSelection, sortedRows, selectedIds],
  );

  useEffect(() => {
    setSelectedIds(new Set());
  }, [rows]);

  useEffect(() => {
    setPage(0);
  }, [sortedRows]);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected = sortedRows.length > 0 && sortedRows.every((r) => prev.has(r.enhetId));
      const next = new Set(prev);
      if (allSelected) {
        sortedRows.forEach((r) => next.delete(r.enhetId));
      } else {
        sortedRows.forEach((r) => next.add(r.enhetId));
      }
      return next;
    });
  }, [sortedRows]);

  const toggleRow = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleBulkAction = async () => {
    setIsBusy(true);
    setActionError(null);
    try {
      if (onBulkFjern) {
        await onBulkFjern(Array.from(selectedIds));
      } else if (onBulkLeggTil) {
        const items = rows
          .filter((r) => selectedIds.has(r.enhetId))
          .map((r) => ({ enhetId: r.enhetId, fraOgMedDato: r.fraOgMedDato }));
        await onBulkLeggTil(items);
      }
      setShowConfirm(false);
      setSelectedIds(new Set());
    } catch (e: any) {
      setActionError(e?.response?.data?.detail ?? e?.message ?? 'Ukjent feil');
    } finally {
      setIsBusy(false);
    }
  };

  const sortIcon = (col: keyof InnkjopPreviewEndring) =>
    sortCol === col ? sortDir === 'asc' ? '▲' : '▼' : <span style={{ opacity: 0.3 }}>▲</span>;
  const sortThStyle: React.CSSProperties = {
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  };

  if (rows.length === 0) {
    return <p className="text-muted">{emptyText}</p>;
  }
  return (
    <>
      <Modal show={showConfirm} onHide={() => !isBusy && setShowConfirm(false)}>
        <Modal.Header closeButton={!isBusy}>
          <Modal.Title>
            {isLeggTil ? 'Bekreft innmelding til INNK' : 'Bekreft fjerning av INNK-medlemskap'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {isLeggTil
              ? 'Er du sikker på at du vil legge til INNK-medlemskap for '
              : 'Er du sikker på at du vil fjerne INNK-medlemskap for '}
            <strong>{selectedIds.size}</strong> enhet{selectedIds.size !== 1 ? 'er' : ''}?
          </p>
          <p className="text-muted small">
            Handlingen logger endringen i enhetloggen og kan ikke angres automatisk.
          </p>
          {actionError && <Alert variant="danger">{actionError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)} disabled={isBusy}>
            Avbryt
          </Button>
          <Button
            variant={isLeggTil ? 'success' : 'danger'}
            onClick={handleBulkAction}
            disabled={isBusy}
          >
            {isBusy ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {isLeggTil ? 'Legger til…' : 'Fjerner…'}
              </>
            ) : isLeggTil ? (
              'Legg til'
            ) : (
              'Fjern'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      {hasSelection && selectedIds.size > 0 && (
        <div className="mb-3 d-flex align-items-center gap-2">
          <Button
            variant={isLeggTil ? 'success' : 'danger'}
            size="sm"
            onClick={() => {
              setActionError(null);
              setShowConfirm(true);
            }}
          >
            {isLeggTil
              ? `Legg til INNK-medlemskap for ${selectedIds.size} valgte`
              : `Fjern INNK-medlemskap for ${selectedIds.size} valgte`}
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={() => setSelectedIds(new Set())}>
            Fjern markering
          </Button>
        </div>
      )}
      <div className="d-flex flex-wrap gap-2 mb-2 align-items-end">
        <Form.Control
          size="sm"
          placeholder="Søk navn / statistikknr…"
          style={{ maxWidth: 220 }}
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
        />
        {uniqueGrupperinger.length > 1 && (
          <Form.Select
            size="sm"
            style={{ maxWidth: 220 }}
            value={filterGruppering}
            onChange={(e) => setFilterGruppering(e.target.value)}
          >
            <option value="">Alle innkjøpsgrupper</option>
            {uniqueGrupperinger.map((g) => (
              <option key={g} value={g!}>
                {g}
              </option>
            ))}
          </Form.Select>
        )}
        {uniqueBransjer.length > 1 && (
          <Form.Select
            size="sm"
            style={{ maxWidth: 200 }}
            value={filterBransje}
            onChange={(e) => setFilterBransje(e.target.value)}
          >
            <option value="">Alle bransjer</option>
            {uniqueBransjer.map((b) => (
              <option key={b} value={b!}>
                {b}
              </option>
            ))}
          </Form.Select>
        )}
        {uniqueHandlinger.length > 1 && (
          <Form.Select
            size="sm"
            style={{ maxWidth: 180 }}
            value={filterHandling}
            onChange={(e) => setFilterHandling(e.target.value)}
          >
            <option value="">Alle handlinger</option>
            {uniqueHandlinger.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </Form.Select>
        )}
        <Form.Select
          size="sm"
          style={{ maxWidth: 160 }}
          value={filterBadge}
          onChange={(e) => setFilterBadge(e.target.value)}
        >
          <option value="">Alle merker</option>
          <option value="DLVRY">DLVRY</option>
          <option value="INNK">INNK</option>
          <option value="NHO">NHO Reiseliv</option>
          <option value="KNIF">KNIF</option>
          <option value="NORES">NORES</option>
          <option value="OI">OI</option>
          <option value="Nedlagt">Nedlagt</option>
        </Form.Select>
        {isFiltered && (
          <span className="text-muted small align-self-center">
            {sortedRows.length} / {rows.length}
          </span>
        )}
        {isFiltered && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setFilterSearch('');
              setFilterGruppering('');
              setFilterBransje('');
              setFilterHandling('');
              setFilterBadge('');
            }}
          >
            Nullstill filter
          </Button>
        )}
      </div>
      {sortedRows.length === 0 ? (
        <p className="text-muted">Ingen treff for valgt filter.</p>
      ) : (
        <>
          <Table striped bordered hover size="sm" responsive style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                {hasSelection && (
                  <th style={{ width: 36, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={allVisibleSelected}
                      ref={(el: HTMLInputElement | null) => {
                        if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected;
                      }}
                      onChange={toggleAll}
                      title="Velg alle synlige"
                    />
                  </th>
                )}
                <th onClick={() => handleSort('statistikknr')} style={sortThStyle}>
                  Statistikknr {sortIcon('statistikknr')}
                </th>
                <th onClick={() => handleSort('lopenr')} style={sortThStyle}>
                  Løpenr {sortIcon('lopenr')}
                </th>
                <th onClick={() => handleSort('enhetsnavn')} style={sortThStyle}>
                  Enhetsnavn {sortIcon('enhetsnavn')}
                </th>
                <th onClick={() => handleSort('hovedBransje')} style={sortThStyle}>
                  Bransje {sortIcon('hovedBransje')}
                </th>
                <th onClick={() => handleSort('innkjopGrupperingNavn')} style={sortThStyle}>
                  Innkjøpsgruppe {sortIcon('innkjopGrupperingNavn')}
                </th>
                <th onClick={() => handleSort('fraOgMedDato')} style={sortThStyle}>
                  Fra dato {sortIcon('fraOgMedDato')}
                </th>
                {showDatoer && (
                  <th onClick={() => handleSort('oppstartsdato')} style={sortThStyle}>
                    Oppstart {sortIcon('oppstartsdato')}
                  </th>
                )}
                <th onClick={() => handleSort('arsak')} style={sortThStyle}>
                  Årsak {sortIcon('arsak')}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row) => (
                <EnhetsRow
                  key={row.enhetId || row.statistikknr}
                  row={row}
                  showDatoer={showDatoer}
                  hasSelection={hasSelection}
                  isSelected={selectedIds.has(row.enhetId)}
                  onToggle={toggleRow}
                  onInfo={onRowClick}
                />
              ))}
            </tbody>
          </Table>
          {totalPages > 1 && (
            <div className="d-flex align-items-center justify-content-between mt-2">
              <span className="text-muted small">
                Viser {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, sortedRows.length)}{' '}
                av {sortedRows.length}
              </span>
              <div className="d-flex gap-2 align-items-center">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ‹
                </Button>
                <span className="small">
                  {page + 1} / {totalPages}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={page === totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  ›
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

const EndringTableMemo = React.memo(EndringTable);

const InnkjopSync = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InnkjopPreviewResultDto | null>(null);
  const [showIkkeFunnet, setShowIkkeFunnet] = useState(false);
  const [selectedEnhet, setSelectedEnhet] = useState<InnkjopPreviewEndring | null>(null);

  const leggesToilRef = useRef<HTMLDivElement>(null);
  const fjernesRef = useRef<HTMLDivElement>(null);
  const ikkeFunnetRef = useRef<HTMLDivElement>(null);
  const uavklartRef = useRef<HTMLDivElement>(null);

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>, expand?: () => void) => {
    expand?.();
    setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const handleHentPreview = async () => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await InnkjopSyncApi.getPreview();
      setResult(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Ukjent feil');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkLeggTil = useCallback(
    async (items: Array<{ enhetId: number; fraOgMedDato: Date }>) => {
      await InnkjopSyncApi.leggTilInnkBulk(items);
      const data = await InnkjopSyncApi.getPreview();
      setResult(data);
    },
    [],
  );

  const handleBulkFjern = useCallback(async (ids: number[]) => {
    await InnkjopSyncApi.fjernInnkBulk(ids);
    const data = await InnkjopSyncApi.getPreview();
    setResult(data);
  }, []);

  const handleModalClose = useCallback(() => setSelectedEnhet(null), []);

  return (
    <PageWrapper header="INNK synkronisering – forhåndsvisning">
      <AvtaleDetaljerModal enhet={selectedEnhet} onClose={handleModalClose} />

      <Row className="mb-3">
        <Col>
          <p className="text-muted">
            Sammenligner innmeldte enheter fra ekstern INNK-master (salgsdatainfoapi) mot aktivt
            INNKAS-medlemskap i Kjederegisteret. Ingen endringer gjøres.
          </p>
          <div className="d-flex align-items-end gap-3 mb-3">
            <Button variant="primary" onClick={handleHentPreview} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Henter...
                </>
              ) : (
                'Hent forhåndsvisning'
              )}
            </Button>
          </div>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <AutoSyncPanel />
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger">{error}</Alert>
          </Col>
        </Row>
      )}

      {result && (
        <>
          <Row className="mb-3 g-2">
            <Col xs="auto">
              <Badge
                bg="success"
                className="fs-6 py-2 px-3"
                style={{ cursor: 'pointer' }}
                onClick={() => scrollTo(leggesToilRef)}
              >
                +{result.antallSkalLeggesTil} Skal legges til
              </Badge>
            </Col>
            <Col xs="auto">
              <Badge
                bg="danger"
                className="fs-6 py-2 px-3"
                style={{ cursor: 'pointer' }}
                onClick={() => scrollTo(fjernesRef)}
              >
                -{result.antallSkalFjernes} Skal fjernes
              </Badge>
            </Col>
            <Col xs="auto">
              <Badge bg="secondary" className="fs-6 py-2 px-3">
                {result.antallUendret} Uendret
              </Badge>
            </Col>
            <Col xs="auto">
              <Badge
                bg="warning"
                text="dark"
                className="fs-6 py-2 px-3"
                style={{ cursor: result.antallIkkeFunnet > 0 ? 'pointer' : undefined }}
                onClick={() =>
                  result.antallIkkeFunnet > 0 &&
                  scrollTo(ikkeFunnetRef, () => setShowIkkeFunnet(true))
                }
              >
                {result.antallIkkeFunnet} Ikke funnet
              </Badge>
            </Col>
            <Col xs="auto">
              <Badge
                bg="info"
                className="fs-6 py-2 px-3"
                style={{ cursor: result.antallUavklart > 0 ? 'pointer' : undefined }}
                onClick={() => result.antallUavklart > 0 && scrollTo(uavklartRef)}
              >
                {result.antallUavklart} Uavklart
              </Badge>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col>
              <div ref={leggesToilRef}>
                <Card>
                  <Card.Header className="bg-success text-white">
                    Skal legges til ({result.antallSkalLeggesTil})
                  </Card.Header>
                  <Card.Body>
                    <EndringTableMemo
                      rows={result.skalLeggesTil}
                      emptyText="Ingen enheter skal legges til."
                      onRowClick={setSelectedEnhet}
                      showDatoer
                      onBulkLeggTil={handleBulkLeggTil}
                    />
                  </Card.Body>
                </Card>
              </div>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col>
              <div ref={fjernesRef}>
                <Card>
                  <Card.Header className="bg-danger text-white">
                    Skal fjernes ({result.antallSkalFjernes})
                  </Card.Header>
                  <Card.Body>
                    <EndringTableMemo
                      rows={result.skalFjernes}
                      emptyText="Ingen enheter skal fjernes."
                      onRowClick={setSelectedEnhet}
                      showDatoer
                      onBulkFjern={handleBulkFjern}
                    />
                  </Card.Body>
                </Card>
              </div>
            </Col>
          </Row>

          {result.antallUavklart > 0 && (
            <Row className="mb-4">
              <Col>
                <div ref={uavklartRef}>
                  <Card>
                    <Card.Header className="bg-info text-white">
                      Uavklart – krever avgjørelse ({result.antallUavklart})
                    </Card.Header>
                    <Card.Body>
                      <p className="text-muted small mb-2">
                        Disse enhetene har status <strong>UAVKLART</strong> i ekstern INNK-master.
                        Brukeren må avgjøre om de skal legges til eller fjernes som INNK-medlemmer.
                      </p>
                      <EndringTableMemo
                        rows={result.uavklart}
                        emptyText="Ingen uavklarte enheter."
                        onRowClick={setSelectedEnhet}
                        showDatoer
                      />
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            </Row>
          )}

          {result.antallIkkeFunnet > 0 && (
            <Row className="mb-4">
              <Col>
                <div ref={ikkeFunnetRef}>
                  <Card>
                    <Card.Header
                      className="bg-warning text-dark d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setShowIkkeFunnet((v) => !v)}
                    >
                      <span>
                        Ikke funnet i Kjederegisteret (statistikknr ukjent) (
                        {result.antallIkkeFunnet})
                      </span>
                      <span>{showIkkeFunnet ? '▲' : '▼'}</span>
                    </Card.Header>
                    {showIkkeFunnet && (
                      <Card.Body>
                        <EndringTableMemo
                          rows={result.ikkeFunnet}
                          emptyText="Ingen ukjente statistikknummer."
                          onRowClick={setSelectedEnhet}
                          showDatoer
                        />
                      </Card.Body>
                    )}
                  </Card>
                </div>
              </Col>
            </Row>
          )}
        </>
      )}
    </PageWrapper>
  );
};

export default InnkjopSync;
